import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Heading,
  Input,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select } from "chakra-react-select";
import { endOfMonth, format, subMonths } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import {
  fetchCustomerData,
  patchCustomerData,
  triggerRevenueCalculation,
} from "../../api/industryInsights";
import { Context } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";
import { customCurrencyFormat } from "../../utils";
import { GenericModal } from "../generic/modal";

const generateMonthOptions = () => {
  const options = [];
  const now = new Date();

  for (let i = 0; i < 16; i++) {
    const date = endOfMonth(subMonths(now, i));
    options.push({
      label: format(date, "MMMM yyyy"),
      value: date.toISOString().split("T")[0],
    });
  }

  return options;
};

export const InsightCustomerListing = ({ turnBack }) => {
  const [loading, setLoading] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [specificFilter, setSpecificFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);
  const [cursor, setCursor] = useState(1);
  const [page, setPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [revenueFilter, setRevenueFilter] = useState("fiftyk");
  const [sort, setSort] = useState("descRev");

  const {
    isOpen: isTaskOpen,
    onOpen: onTaskOpen,
    onClose: onTaskClose,
  } = useDisclosure();

  const context = React.useContext(Context);

  const monthOptions = generateMonthOptions();
  const defaultValue = monthOptions[0];

  useEffect(() => {
    const refreshCustomerList = (params) => {
      fetchCustomerData(params)
        .then((data) => {
          setCustomerList(data.results);
          setNextCursor(data.next);
          setPreviousCursor(data.previous);
        })
        .catch((err) => {
          console.error("Failed to fetch customer List:", err);
        });

      setLoading(false);
    };

    const params = {
      date: dateFilter.value || defaultValue.value,
      revenue: revenueFilter,
      nameFilter: specificFilter,
      sort: sort,
      cursor: cursor,
      page: page,
    };
    refreshCustomerList(params);
  }, [
    dateFilter,
    specificFilter,
    revenueFilter,
    cursor,
    page,
    defaultValue.value,
    sort,
  ]);

  const handleSubmit = async () => {
    if (!selectedMonth) {
      return context.fireToast("Missing", "Please select a month", "warning");
    }

    try {
      await triggerRevenueCalculation(selectedMonth);
      context.fireToast("Success", "Calculation triggered", "success");
      onTaskClose();
    } catch (error) {
      console.error("Error triggering calculation:", error);
      context.fireToast("Error", "Something went wrong", "error");
    }
  };

  const revenueGroupMap = useMemo(
    () => ({
      fiftyk: "Under $50k",
      hundredk: "$50k–$100k",
      onefiftyk: "$100k–$150k",
      twofiftyk: "$150k–$250k",
      overtwofiftyk: "Over $250k",
    }),
    []
  );

  const sortGroupMap = useMemo(
    () => ({
      descRev: "Revenue - Descending",
      descProf: "Profit - Descending",
      descProfPercent: "Profitability - Descending",
      ascRev: "Revenue - Ascending",
      ascProf: "Profit - Ascending",
      ascProfPercent: "Profitability - Ascending",
    }),
    []
  );

  const revenueGroupOptions = useMemo(
    () =>
      Object.entries(revenueGroupMap).map(([value, label]) => ({
        value,
        label,
      })),
    [revenueGroupMap]
  );

  const sortGroupOptions = useMemo(
    () =>
      Object.entries(sortGroupMap).map(([value, label]) => ({
        value,
        label,
      })),
    [sortGroupMap]
  );

  const handleNextPage = () => {
    if (nextCursor) {
      const urlObj = new URL(nextCursor);
      setCursor(urlObj.searchParams.get("cursor"));
      setPage(urlObj.searchParams.get("page"));
    }
  };

  const handlePreviousPage = () => {
    if (previousCursor) {
      const urlObj = new URL(previousCursor);
      setCursor(urlObj.searchParams.get("cursor"));
      setPage(urlObj.searchParams.get("page"));
    }
  };

  return (
    <Container maxW="100%" bg="brand.50" h="calc(91vh)">
      {loading && (
        <FontAwesomeIcon
          className="float-end fa-spin"
          icon={faSpinner}
          color="green"
        />
      )}
      <TaskModal
        isOpen={isTaskOpen}
        onClose={onTaskClose}
        setSelectedMonth={setSelectedMonth}
        handleSubmit={handleSubmit}
      />
      <Stack direction="column" spacing={2} align="center">
        <CustomTableContainer
          width="100%"
          minHeight="650px"
          maxHeight="calc(750px)"
        >
          <Flex pb="2">
            <Box>
              <Text
                onClick={() => turnBack(false)}
                color={"#536DEC"}
                cursor={"pointer"}
              >
                {"<-- Back to Manage Industry Insights"}
              </Text>
              <Heading as="h3" size="lg">
                Manage Customers Applied to Insights
              </Heading>
            </Box>
            <Spacer />
            <Box>
              <Button onClick={onTaskOpen}>Calculate Revenue / Profit</Button>
            </Box>
            <Spacer />
            <Box width={"300px"}>
              <Text pb={"10px"}>Filter by Customer Name</Text>
              <Input
                placeholder="Search..."
                onChange={(e) => {
                  setSpecificFilter(e.target.value);
                  setPage(1);
                  setCursor(1);
                }}
              />
            </Box>
          </Flex>

          <Flex pb="5">
            <Box mr={"10px"} zIndex={100}>
              <Text pb={"10px"}>Filter by date</Text>
              <Select
                options={monthOptions}
                placeholder="Select a month"
                value={dateFilter || defaultValue}
                onChange={(option) => {
                  setDateFilter(option);
                  setPage(1);
                  setCursor(1);
                }}
                useBasicStyles
              />
            </Box>
            <Box zIndex={100}>
              <Text pb={"10px"}>Filter by Revenue</Text>
              <Select
                options={revenueGroupOptions}
                placeholder="Select a revenue group"
                value={revenueGroupOptions.find(
                  (opt) => opt.value === revenueFilter
                )}
                onChange={(option) => {
                  setRevenueFilter(option.value);
                  setPage(1);
                  setCursor(1);
                }}
                useBasicStyles
              />
            </Box>
            <Spacer />
            <Box zIndex={100}>
              <Text pb={"10px"}>Sort by Field</Text>
              <Select
                options={sortGroupOptions}
                value={sortGroupOptions.find((opt) => opt.value === sort)}
                placeholder="Sort by..."
                onChange={(option) => {
                  setSort(option.value);
                  setPage(1);
                  setCursor(1);
                }}
                useBasicStyles
              />
            </Box>
          </Flex>

          <Box
            flex="1"
            overflowY="auto"
            maxHeight="calc(480px)"
            bg="white"
          >
            <Table variant="unstyled" minWidth="100%">
              <Thead position="sticky" top={0} zIndex={1} bg="white">
                <Tr>
                  <Th>Customer</Th>
                  <Th>Profitability</Th>
                  <Th>Profit</Th>
                  <Th>Revenue</Th>
                  <Th>In Top 1%</Th>
                  <Th>In Top 10%</Th>
                  <Th>In Average</Th>
                </Tr>
              </Thead>
              <Tbody>
                {customerList.map((customer) => (
                  <CustomerRow key={customer?.id} customer={customer} />
                ))}
              </Tbody>
            </Table>
          </Box>
          <Stack direction="row" spacing={4} mt={4} justifyContent="center">
            {previousCursor && (
              <Button onClick={handlePreviousPage}>Previous</Button>
            )}
            {nextCursor && (
              <Button onClick={handleNextPage} disabled={!nextCursor}>
                Next
              </Button>
            )}
          </Stack>
        </CustomTableContainer>
      </Stack>
    </Container>
  );
};

const CustomerRow = ({ customer }) => {
  const [customerItem, setCustomerItem] = useState(customer);

  const handleToggle = (field, value) => {
    setCustomerItem((prev) => {
      const updated = { ...prev, [field]: value };

      patchCustomerData(prev.id, { [field]: value })
        .then(() => {
          console.log("Updated successfully");
        })
        .catch((err) => {
          console.error("Failed to update:", err);
          setCustomerItem(prev);
        });

      return updated;
    });
  };

  return (
    <>
      <Tr key={customerItem?.id}>
        <Td>{customerItem?.customer?.name}</Td>
        <Td>
          {customCurrencyFormat(
            customerItem?.profit_percentage ?? 0,
            true,
            false,
            true,
            true
          )}
        </Td>
        <Td>
          {customCurrencyFormat(
            customerItem?.profit ?? 0,
            false,
            false,
            true,
            true
          )}
        </Td>
        <Td>
          {customCurrencyFormat(
            customerItem?.revenue ?? 0,
            false,
            false,
            true,
            true
          )}
        </Td>
        <Td>
          <Checkbox
            isChecked={customerItem?.is_in_top_one}
            onChange={(e) => handleToggle("is_in_top_one", e.target.checked)}
          >
            Top 1%
          </Checkbox>
        </Td>
        <Td>
          <Checkbox
            isChecked={customerItem?.is_in_top_ten}
            onChange={(e) => handleToggle("is_in_top_ten", e.target.checked)}
          >
            Top 10%
          </Checkbox>
        </Td>
        <Td>
          <Checkbox
            isChecked={customerItem?.is_in_average}
            onChange={(e) => handleToggle("is_in_average", e.target.checked)}
          >
            Average
          </Checkbox>
        </Td>
      </Tr>
    </>
  );
};

const TaskModal = ({ isOpen, onClose, setSelectedMonth, handleSubmit }) => {
  const past12Months = Array.from({ length: 12 }, (_, i) => {
    const date = endOfMonth(subMonths(new Date(), i + 1));
    return {
      label: format(date, "MMMM yyyy"),
      value: format(date, "yyyy-MM-dd"),
    };
  });

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Calculate latest revenue / profit"
      body={
        <>
          <Text pb={"10px"}>Update records up to month</Text>
          <Select
            options={past12Months}
            placeholder="Select a month"
            onChange={(option) => setSelectedMonth(option.value)}
            useBasicStyles
          />
        </>
      }
      submitButton={
        <Button onClick={handleSubmit} ml={3}>
          Update
        </Button>
      }
    ></GenericModal>
  );
};
